"use client";

import { useRef } from "react";
import Image from "next/image";
import { PortableText, toPlainText, type PortableTextComponents } from "@portabletext/react";
import { stegaClean } from "@sanity/client/stega";

import { GroqImage } from "@/helpers/Image";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import type { PageRizAffiliateSchema } from "@/sanity/pages/PageRizAffiliateTemplate";
import { measuredRichText } from "./richtext";
import ChevronCta from "./ChevronCta";
import Eyebrow from "./Eyebrow";
import HeroCandles from "./hero/HeroCandles";
import HeroChips from "./hero/HeroChips";
import HeroTicker from "./hero/HeroTicker";
import CodeBox from "./CodeBox";
import { splitWords } from "./hero/reveal";
import { useHeroIntro } from "./hero/useHeroIntro";

import logo from "@/public/riz-affiliate/hero-logo.svg";
import dots from "@/public/patrick-affiliate/hero-grid.png";

const titleText: PortableTextComponents = {
  ...measuredRichText,
  block: ({ children }) => <span className="block">{splitWords(children)}</span>,
};

const GRID_LINES = [
  "linear-gradient(to right, rgba(0,56,40,0.108) 1px, transparent 1px)",
  "linear-gradient(to bottom, rgba(0,56,40,0.108) 1px, transparent 1px)",
].join(", ");
const GRID_FADE_X =
  "linear-gradient(90deg, #000 0%, rgba(0,0,0,0.8) 15%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.345) 45%, rgba(0,0,0,0.145) 55%, transparent 61%)";
const GRID_FADE_Y =
  "linear-gradient(180deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.84) 11.76%, rgba(0,0,0,0.74) 23.5%, rgba(0,0,0,0.66) 35.3%, rgba(0,0,0,0.573) 47%, rgba(0,0,0,0.48) 58.8%, rgba(0,0,0,0.375) 70.6%, rgba(0,0,0,0.265) 82.4%, rgba(0,0,0,0.236) 94%)";

// The studio wall and its grid only exist on desktop; phones get a white photo frame.
const STAGE_SIZES = "(min-width: 993px) 100vw, 1px";
const PORTRAIT_SIZES = "(min-width: 993px) 68vw, (min-width: 640px) 752px, 175vw";

export default function HeroSection({ data }: { data: PageRizAffiliateSchema }) {
  const rootRef = useRef<HTMLElement>(null);
  useHeroIntro(rootRef);

  const language = stegaClean(data?.settings?.language);
  const translations: Record<string, string[] | undefined> = data?.scrollingWords?.wordsI18n ?? {};
  const i18nWords = language && language !== DEFAULT_LANGUAGE ? translations[language] : undefined;
  const tickerWords = i18nWords?.length ? i18nWords : data?.scrollingWords?.words;
  const eyebrow = data?.heroChipText?.length ? toPlainText(data.heroChipText) : "";

  if (!eyebrow && !data?.heroHeading?.length && !data?.heroCTA && !data?.heroBackground && !data?.heroPortrait) return null;

  return (
    <section
      ref={rootRef}
      className="riz-hero is-pending relative isolate overflow-hidden bg-white">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 top-[8rem] opacity-20 lg:hidden"
        style={{ backgroundImage: `url(${dots.src})`, backgroundSize: "3.5rem 3.5rem", backgroundPosition: "top left" }}
      />

      <div className="relative lg:aspect-[1920/781]">
        <div className="z-[6] border-b max-lg:relative max-lg:border-black/[0.11] max-lg:py-[2.5rem] lg:absolute lg:inset-x-0 lg:top-0 lg:h-[8.375rem] lg:border-[#0B1512]/[0.08] lg:bg-white/[0.35] lg:backdrop-blur-[3.35px]">
          <div className="mx-auto flex h-full w-[min(150rem,100%)] items-center max-lg:justify-center lg:justify-between">
            <Image
              src={logo}
              alt="IQ Capital"
              priority
              className="h-[2.915rem] w-[15.738rem]"
            />
            {data?.heroOnAirText && (
              <p className="m-0 h-[4.25rem] items-center gap-[1rem] rounded-[0.5rem] border border-white/[0.12] bg-[#0B1512]/[0.92] px-[1.5rem] font-geist text-[1.75rem] font-[500] leading-[2.5rem] text-white/[0.65] backdrop-blur-[1.5rem] max-lg:hidden lg:flex">
                <span
                  aria-hidden
                  className="h-[1rem] w-[1rem] shrink-0 rounded-full bg-[#E5484D]"
                />
                {data.heroOnAirText}
              </p>
            )}
          </div>
        </div>

        <div className="pointer-events-none z-[5] max-lg:relative max-lg:pt-[4rem] lg:absolute lg:inset-x-0 lg:top-[23rem]">
          <div className="pointer-events-auto mx-auto flex flex-col max-lg:w-[min(49.75rem,100%)] max-lg:items-center lg:w-[min(150rem,100%)] lg:items-start">
            {eyebrow && (
              <div
                data-intro="eyebrow"
                className="mb-[2rem]">
                <div className="lg:hidden">
                  <Eyebrow
                    text={eyebrow}
                    tone="light"
                  />
                </div>
                <div className="max-lg:hidden">
                  <Eyebrow
                    text={eyebrow}
                    tone="hero"
                  />
                </div>
              </div>
            )}
            {data?.heroHeading?.length > 0 && (
              <h1
                data-intro="title"
                className="riz-hero-title m-0 font-instrument-sans font-[500] [text-wrap:wrap] max-lg:w-[min(41.375rem,100%)] max-lg:text-center max-lg:text-[5.5rem] max-lg:leading-[6rem] max-lg:tracking-[-0.2985rem] max-lg:text-black max-lg:[--rv-lh:6rem] lg:w-[min(53.375rem,100%)] lg:text-left lg:text-[7rem] lg:leading-[7rem] lg:tracking-[-0.375rem] lg:text-[#0B1512] lg:[--rv-lh:7rem]">
                <PortableText
                  value={data.heroHeading}
                  components={titleText}
                />
              </h1>
            )}
            {data?.heroSubtitle && (
              <p
                data-intro="lead"
                className="m-0 font-instrument-sans font-[500] [text-wrap:wrap] max-lg:mt-[2rem] max-lg:w-full max-lg:text-center max-lg:text-[2rem] max-lg:leading-[3rem] max-lg:text-black/[0.64] lg:mt-[3rem] lg:w-[min(52rem,100%)] lg:text-left lg:text-[2.25rem] lg:leading-[3.5rem] lg:text-[#0B1512]/[0.72]">
                {splitWords(data.heroSubtitle)}
              </p>
            )}
            {(data?.heroCTA || stegaClean(data?.code)?.trim()) && (
              <div
                data-intro="cta"
                className="mt-[3rem] flex max-lg:w-full max-lg:flex-col max-lg:items-stretch max-lg:gap-[1.5rem] lg:flex-row lg:items-center lg:gap-[2rem]">
                {data?.heroCTA && (
                  <ChevronCta
                    data={data.heroCTA}
                    language={data?.settings?.language}
                    className="max-lg:[&>span]:pr-[4.25rem] max-lg:[&>span>span:last-child]:w-[5.75rem] lg:[&>span]:rounded-[1.5rem]"
                  />
                )}
                <CodeBox
                  variant="hero"
                  label={data?.codeLabel}
                  code={data?.code}
                  copyLabel={data?.codeCopyLabel}
                />
              </div>
            )}
          </div>
        </div>

        <div className="overflow-hidden max-lg:relative max-lg:h-[52.5rem] max-lg:bg-white lg:absolute lg:inset-0">
          {/* Desktop: the 1920×850 studio stage. Phones: the 430×420 photo frame, white, no wall or grid; centered between 640 and 992px, where Figma has no frame. */}
          <div className="absolute max-lg:inset-0 sm:max-lg:mx-auto sm:max-lg:w-[53.75rem] lg:inset-x-0 lg:top-0 lg:aspect-[1920/850]">
            {data?.heroBackground && (
              <GroqImage
                src={data.heroBackground}
                alt=""
                priority
                fetchPriority="high"
                quality={100}
                sizes={STAGE_SIZES}
                style={{ width: "100%" }}
                className="absolute inset-0 !h-full object-cover max-lg:hidden"
              />
            )}
            <div
              aria-hidden
              className="absolute inset-0 max-lg:hidden"
              style={{ maskImage: GRID_FADE_X, WebkitMaskImage: GRID_FADE_X }}>
              <div
                className="absolute inset-0"
                style={{ maskImage: GRID_FADE_Y, WebkitMaskImage: GRID_FADE_Y }}>
                <div
                  className="absolute inset-x-0 bottom-0 top-[-4.5294%]"
                  style={{ backgroundImage: GRID_LINES, backgroundSize: "2.5% 5.4024%" }}
                />
              </div>
            </div>
            {/* The Figma chart carries a 1px layer blur (SVG stdDeviation 0.51). */}
            <HeroCandles className="absolute aspect-[1088/548] [filter:blur(0.064rem)] max-lg:left-[-18.47rem] max-lg:top-[1.903rem] max-lg:w-[77.792rem] lg:left-[52.1875%] lg:top-[6.8471%] lg:w-[48.1667%]" />
            {data?.heroPortrait && (
              <GroqImage
                src={data.heroPortrait}
                alt=""
                priority
                fetchPriority="high"
                quality={100}
                sizes={PORTRAIT_SIZES}
                style={{ width: "68.0729%" }}
                className="absolute !h-auto max-lg:left-[-36.281rem] max-lg:top-0 max-lg:!h-[52.5rem] max-lg:!w-auto max-lg:max-w-none lg:left-[23.2813%] lg:top-[6.1176%]"
              />
            )}
          </div>
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-[5.375rem] bg-gradient-to-b from-white to-white/0 lg:hidden"
          />
          <HeroChips
            chips={data?.heroChips}
            className="absolute max-lg:left-[calc(50%-24.875rem)] max-lg:top-[31.25rem] max-lg:flex max-lg:flex-col max-lg:items-start max-lg:gap-[1rem] lg:inset-0"
          />
        </div>
      </div>

      <HeroTicker
        words={tickerWords}
        className="relative max-lg:h-[8.625rem] max-lg:opacity-60 lg:h-[6.75rem] lg:border-t lg:border-black/[0.10] lg:bg-white lg:shadow-[0_-0.75rem_2.48125rem_rgba(0,0,0,0.07)]"
      />

      <style jsx>{`
        .riz-hero :global(p) {
          -webkit-font-smoothing: antialiased;
        }
        .riz-hero :global(.rv-mask),
        .riz-hero :global(.rv-unit) {
          display: inline-block;
          font-family: inherit;
        }
        .riz-hero :global(.rv-mask) {
          clip-path: inset(-0.25em -0.3em -0.12em -0.3em);
        }
        .riz-hero.is-done :global(.rv-mask) {
          clip-path: none;
        }
        .riz-hero-title :global(span) {
          line-height: 0;
        }
        .riz-hero-title :global(.rv-mask) {
          line-height: var(--rv-lh) !important;
          font-family: var(--font-instrument-sans), sans-serif !important;
        }
        .riz-hero-title :global(.rv-serif) {
          font-family: var(--font-blacker), serif !important;
        }
        .riz-hero-title :global(.hl-texture) {
          display: none !important;
        }
        .riz-hero-title {
          --hl-wash: 5.5rem;
          --hl-ext: 0rem;
          --hl-dy: 0rem;
          --hl-ctr: 0px;
          --hl-tracking: -0.058em;
          --hl-ml: 1.68rem;
          --hl-mr: 0.278rem;
          --hl-pl: 0.03rem;
          --hl-pr: 1.617rem;
        }
        .riz-hero-title :global(.hl-wash) {
          background-color: #e3f2f0 !important;
        }
        .riz-hero-title :global(span:has(> .hl-wash)) {
          --hl-sr: 0.75rem !important;
          --hl-sl: 0.6395rem !important;
          color: #0e3b2c !important;
        }
        @media (min-width: 993px) {
          .riz-hero-title {
            --hl-wash: 7.75rem;
            --hl-ext: -0.5rem;
            --hl-dy: 0.125rem;
            --hl-ctr: 0px;
            --hl-tracking: -0.4rem;
            --hl-ml: 0.9rem;
            --hl-mr: 0px;
            --hl-pl: 1.025rem;
            --hl-pr: 1.8rem;
          }
          .riz-hero-title :global(.hl-wash) {
            background-color: #e2f3ef !important;
          }
          .riz-hero-title :global(span:has(> .hl-wash)) {
            color: #003828 !important;
            --hl-r: 1px !important;
            --hl-sr: 0.5rem !important;
            --hl-sl: 0.5rem !important;
          }
          .riz-hero-title :global(.hl-tick-l) {
            top: calc(var(--hl-top) + var(--hl-sl)) !important;
            right: calc(100% + var(--hl-p-l) - 0.3125rem) !important;
          }
          .riz-hero-title :global(.hl-tick-r) {
            top: calc(var(--hl-top) - var(--hl-sr)) !important;
            left: calc(100% + var(--hl-p-r) - 0.3125rem) !important;
          }
          .riz-hero-title :global(.hl-tick-r > span:last-child) {
            height: calc(var(--hl-w) - var(--hl-sr)) !important;
          }
          .riz-hero-title :global(.hl-tick-l span),
          .riz-hero-title :global(.hl-tick-r span) {
            background-color: #4dc7a3 !important;
          }
        }
        @media (prefers-reduced-motion: no-preference) {
          .riz-hero.is-pending :global([data-intro]) {
            visibility: hidden;
          }
        }
      `}</style>
    </section>
  );
}
